import { DEFAULT_DYNAMICS, type BrushPreset, type TipImage } from '../brush/types';
import { createId } from '../core/ids';

export interface AbrImportResult {
  /** Sampled tip bitmaps found in the file. */
  tips: TipImage[];
  /** One preset per brush, referencing the tips by id. */
  presets: BrushPreset[];
  /** Brushes the parser recognised but could not decode, for a status message. */
  skipped: number;
  /** Format version found in the header. */
  version: number;
}

const MIN_VERSION = 1;
const MAX_SAMPLE_DIMENSION = 8192;
const LEGACY_HEADER_BYTES = 4;
const LEGACY_BRUSH_HEADER_BYTES = 6;
const MODERN_HEADER_BYTES = 4;
const SECTION_HEADER_BYTES = 12;
const MODERN_BRUSH_HEADER_BYTES = 4;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function align4(value: number): number {
  return (value + 3) & ~3;
}

function fallbackBrushName(nameHint: string | undefined, index: number): string {
  return `${nameHint ?? 'Brush'} ${index}`;
}

function readAscii(bytes: Uint8Array, offset: number, length: number): string | null {
  if (offset < 0 || length < 0 || offset + length > bytes.length) return null;
  let value = '';
  for (let i = 0; i < length; i += 1) value += String.fromCharCode(bytes[offset + i]);
  return value;
}

function readUtf16BeString(
  view: DataView,
  offset: number,
  limit: number,
): { value: string; nextOffset: number } | null {
  if (offset + 4 > limit) return null;
  const characterCount = view.getInt32(offset, false);
  if (characterCount < 0) return null;
  const byteLength = characterCount * 2;
  const start = offset + 4;
  const end = start + byteLength;
  if (end > limit) return null;
  const codeUnits = new Array<number>(characterCount);
  for (let i = 0; i < characterCount; i += 1) {
    codeUnits[i] = view.getUint16(start + i * 2, false);
  }
  return {
    value: String.fromCharCode(...codeUnits),
    nextOffset: end,
  };
}

function readBitmap(
  view: DataView,
  bytes: Uint8Array,
  offset: number,
  limit: number,
  width: number,
  height: number,
  compression: number,
): Uint8Array | null {
  const pixelCount = width * height;
  if (!Number.isSafeInteger(pixelCount) || pixelCount <= 0) return null;
  if (compression === 0) {
    const end = offset + pixelCount;
    if (end > limit) return null;
    return new Uint8Array(bytes.slice(offset, end));
  }
  if (compression !== 1) return null;

  const decoded = new Uint8Array(pixelCount);
  let inputOffset = offset;
  for (let row = 0; row < height; row += 1) {
    let rowOffset = row * width;
    const rowEnd = rowOffset + width;
    while (rowOffset < rowEnd) {
      if (inputOffset >= limit) return null;
      const count = view.getInt8(inputOffset);
      inputOffset += 1;
      if (count >= 0) {
        const literalCount = count + 1;
        if (rowOffset + literalCount > rowEnd || inputOffset + literalCount > limit) return null;
        decoded.set(bytes.subarray(inputOffset, inputOffset + literalCount), rowOffset);
        inputOffset += literalCount;
        rowOffset += literalCount;
        continue;
      }
      if (count === -128) continue;
      const repeatCount = 1 - count;
      if (rowOffset + repeatCount > rowEnd || inputOffset >= limit) return null;
      decoded.fill(bytes[inputOffset], rowOffset, rowOffset + repeatCount);
      inputOffset += 1;
      rowOffset += repeatCount;
    }
  }
  return decoded;
}

function buildPatternPreset(
  name: string,
  imageId: string,
  size: number,
  spacing: number,
): BrushPreset {
  return {
    id: createId('preset'),
    name,
    size,
    tip: {
      kind: 'pattern',
      hardness: 1,
      angle: 0,
      roundness: 1,
      spacing,
      imageId,
    },
    dynamics: { ...DEFAULT_DYNAMICS },
  };
}

function buildCircularPreset(
  name: string,
  size: number,
  hardness: number,
  roundness: number,
  angle: number,
  spacing: number,
): BrushPreset {
  return {
    id: createId('preset'),
    name,
    size,
    tip: {
      kind: 'circular',
      hardness,
      angle,
      roundness,
      spacing,
      imageId: null,
    },
    dynamics: { ...DEFAULT_DYNAMICS },
  };
}

function parseLegacyComputedBrush(
  view: DataView,
  start: number,
  end: number,
  version: number,
  nameHint: string | undefined,
  index: number,
): BrushPreset | null {
  let offset = start;
  if (offset + 6 > end) return null;
  offset += 4; // misc
  const spacing = clamp(view.getUint16(offset, false) / 100, 0.01, 3);
  offset += 2;

  let name = fallbackBrushName(nameHint, index);
  if (version === 2) {
    const parsedName = readUtf16BeString(view, offset, end);
    if (!parsedName) return null;
    name = parsedName.value || name;
    offset = parsedName.nextOffset;
  }

  if (offset + 8 > end) return null;
  const diameter = Math.max(MIN_VERSION, view.getUint16(offset, false));
  const roundness = clamp(view.getUint16(offset + 2, false) / 100, 0.05, 1);
  const angle = view.getInt16(offset + 4, false);
  const hardness = clamp(view.getUint16(offset + 6, false) / 100, 0, 1);
  return buildCircularPreset(name, diameter, hardness, roundness, angle, spacing);
}

function parseLegacySampledBrush(
  view: DataView,
  bytes: Uint8Array,
  start: number,
  end: number,
  version: number,
  nameHint: string | undefined,
  index: number,
): { tip: TipImage; preset: BrushPreset } | null {
  let offset = start;
  if (offset + 6 > end) return null;
  offset += 4; // misc
  const spacing = clamp(view.getUint16(offset, false) / 100, 0.01, 3);
  offset += 2;

  let name = fallbackBrushName(nameHint, index);
  if (version === 2) {
    const parsedName = readUtf16BeString(view, offset, end);
    if (!parsedName) return null;
    name = parsedName.value || name;
    offset = parsedName.nextOffset;
  }

  if (offset + 27 > end) return null;
  offset += 1; // antialiasing
  offset += 8; // int16 bounds rectangle
  const top = view.getInt32(offset, false);
  const left = view.getInt32(offset + 4, false);
  const bottom = view.getInt32(offset + 8, false);
  const right = view.getInt32(offset + 12, false);
  offset += 16;
  const width = right - left;
  const height = bottom - top;
  const depth = view.getUint16(offset, false);
  offset += 2;
  const compression = view.getUint8(offset);
  offset += 1;

  if (
    width < 1 ||
    height < 1 ||
    width > MAX_SAMPLE_DIMENSION ||
    height > MAX_SAMPLE_DIMENSION ||
    depth !== 8
  ) {
    return null;
  }

  const bitmap = readBitmap(view, bytes, offset, end, width, height, compression);
  if (!bitmap) return null;

  const tip: TipImage = {
    id: createId('tip'),
    name,
    width,
    height,
    data: bitmap,
  };
  return {
    tip,
    preset: buildPatternPreset(name, tip.id, Math.max(width, height), spacing),
  };
}

function parseLegacyAbr(
  buffer: ArrayBuffer,
  version: number,
  nameHint: string | undefined,
): AbrImportResult {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const count = view.getUint16(2, false);
  const result: AbrImportResult = {
    tips: [],
    presets: [],
    skipped: 0,
    version,
  };

  let offset = LEGACY_HEADER_BYTES;
  for (let index = 1; index <= count; index += 1) {
    if (offset + LEGACY_BRUSH_HEADER_BYTES > buffer.byteLength) {
      result.skipped += count - index + 1;
      break;
    }
    const type = view.getUint16(offset, false);
    const blockLength = view.getInt32(offset + 2, false);
    const blockStart = offset + LEGACY_BRUSH_HEADER_BYTES;
    const blockEnd = blockStart + blockLength;
    if (blockLength < 0 || blockEnd < blockStart || blockEnd > buffer.byteLength) {
      result.skipped += count - index + 1;
      break;
    }

    if (type === 1) {
      const preset = parseLegacyComputedBrush(view, blockStart, blockEnd, version, nameHint, index);
      if (preset) {
        result.presets.push(preset);
      } else {
        result.skipped += 1;
      }
    } else if (type === 2) {
      const parsed = parseLegacySampledBrush(
        view,
        bytes,
        blockStart,
        blockEnd,
        version,
        nameHint,
        index,
      );
      if (parsed) {
        result.tips.push(parsed.tip);
        result.presets.push(parsed.preset);
      } else {
        result.skipped += 1;
      }
    } else {
      result.skipped += 1;
    }

    offset = blockEnd;
  }

  return result;
}

function tryParseModernSampledBrush(
  view: DataView,
  bytes: Uint8Array,
  start: number,
  end: number,
  name: string,
): { tip: TipImage; preset: BrushPreset } | null {
  const attempts = [
    { keyBytes: 37, hasUnknown: true },
    { keyBytes: 0, hasUnknown: true },
    { keyBytes: 37, hasUnknown: false },
  ];

  for (const attempt of attempts) {
    let offset = start + attempt.keyBytes;
    if (offset > end) continue;
    if (attempt.hasUnknown) {
      if (offset + 2 > end) continue;
      offset += 2;
    }
    if (offset + 19 > end) continue;
    const top = view.getInt32(offset, false);
    const left = view.getInt32(offset + 4, false);
    const bottom = view.getInt32(offset + 8, false);
    const right = view.getInt32(offset + 12, false);
    offset += 16;
    const width = right - left;
    const height = bottom - top;
    const depth = view.getUint16(offset, false);
    offset += 2;
    const compression = view.getUint8(offset);
    offset += 1;

    if (
      width < 1 ||
      height < 1 ||
      width > MAX_SAMPLE_DIMENSION ||
      height > MAX_SAMPLE_DIMENSION ||
      depth !== 8 ||
      (compression !== 0 && compression !== 1)
    ) {
      continue;
    }

    const bitmap = readBitmap(view, bytes, offset, end, width, height, compression);
    if (!bitmap) continue;

    const tip: TipImage = {
      id: createId('tip'),
      name,
      width,
      height,
      data: bitmap,
    };
    return {
      tip,
      preset: buildPatternPreset(name, tip.id, Math.min(1000, Math.max(width, height)), 0.25),
    };
  }

  return null;
}

function parseModernSampSection(
  view: DataView,
  bytes: Uint8Array,
  start: number,
  end: number,
  nameHint: string | undefined,
  result: AbrImportResult,
): void {
  let offset = start;
  let brushIndex = 1;

  while (offset + MODERN_BRUSH_HEADER_BYTES <= end) {
    const brushLength = view.getInt32(offset, false);
    const brushStart = offset + MODERN_BRUSH_HEADER_BYTES;
    const brushEnd = brushStart + brushLength;
    if (brushLength < 0 || brushEnd < brushStart || brushEnd > end) {
      result.skipped += 1;
      break;
    }

    const parsed = tryParseModernSampledBrush(
      view,
      bytes,
      brushStart,
      brushEnd,
      fallbackBrushName(nameHint, brushIndex),
    );
    if (parsed) {
      result.tips.push(parsed.tip);
      result.presets.push(parsed.preset);
    } else {
      result.skipped += 1;
    }

    offset = align4(brushEnd);
    brushIndex += 1;
  }
}

function parseModernAbr(
  buffer: ArrayBuffer,
  version: number,
  nameHint: string | undefined,
): AbrImportResult {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const result: AbrImportResult = {
    tips: [],
    presets: [],
    skipped: 0,
    version,
  };

  let offset = MODERN_HEADER_BYTES;
  while (offset + SECTION_HEADER_BYTES <= buffer.byteLength) {
    const signature = readAscii(bytes, offset, 4);
    const key = readAscii(bytes, offset + 4, 4);
    if (signature !== '8BIM' || key === null) break;
    const length = view.getInt32(offset + 8, false);
    const dataStart = offset + SECTION_HEADER_BYTES;
    const dataEnd = dataStart + length;
    if (length < 0 || dataEnd < dataStart || dataEnd > buffer.byteLength) break;
    if (key === 'samp') parseModernSampSection(view, bytes, dataStart, dataEnd, nameHint, result);
    offset = dataEnd;
  }

  return result;
}

/** Parses an ABR file. Throws `Error` with a readable message when the file is not an ABR at all. */
export function parseAbr(buffer: ArrayBuffer, nameHint?: string): AbrImportResult {
  if (buffer.byteLength < 4) throw new Error('The file is not an ABR brush set');
  const version = new DataView(buffer).getUint16(0, false);
  if (![1, 2, 6, 7, 9, 10].includes(version)) {
    throw new Error('Unsupported ABR version ' + version);
  }
  return version <= 2 ? parseLegacyAbr(buffer, version, nameHint) : parseModernAbr(buffer, version, nameHint);
}

function writeUint16(view: DataView, offset: number, value: number): number {
  view.setUint16(offset, value, false);
  return offset + 2;
}

function writeInt32(view: DataView, offset: number, value: number): number {
  view.setInt32(offset, value, false);
  return offset + 4;
}

function writeUtf16Be(view: DataView, offset: number, value: string): number {
  offset = writeInt32(view, offset, value.length);
  for (let i = 0; i < value.length; i += 1) {
    view.setUint16(offset, value.charCodeAt(i), false);
    offset += 2;
  }
  return offset;
}

function buildSampledBrushPayload(brush: {
  name: string;
  tip: TipImage;
  spacing: number;
}): Uint8Array {
  const spacing = clamp(Math.round(brush.spacing * 100), 1, 1000);
  const width = brush.tip.width;
  const height = brush.tip.height;
  const nameBytes = brush.name.length * 2;
  const rawBytes = width * height;
  const unpaddedLength = 4 + 2 + 4 + nameBytes + 1 + 8 + 16 + 2 + 1 + rawBytes;
  const payloadLength = unpaddedLength + (unpaddedLength % 2);
  const payload = new Uint8Array(payloadLength);
  const view = new DataView(payload.buffer);
  let offset = 0;

  offset = writeInt32(view, offset, 0);
  offset = writeUint16(view, offset, spacing);
  offset = writeUtf16Be(view, offset, brush.name);
  payload[offset] = 1;
  offset += 1;
  offset += 8; // int16 bounds rectangle, all zeros
  offset = writeInt32(view, offset, 0);
  offset = writeInt32(view, offset, 0);
  offset = writeInt32(view, offset, height);
  offset = writeInt32(view, offset, width);
  offset = writeUint16(view, offset, 8);
  payload[offset] = 0;
  offset += 1;
  payload.set(brush.tip.data, offset);

  return payload;
}

/** Serialises sampled tips as an ABR version 2 file that Photoshop and Photopea can open. */
export function writeAbr(brushes: { name: string; tip: TipImage; spacing: number }[]): ArrayBuffer {
  const payloads = brushes.map(buildSampledBrushPayload);
  const totalLength = payloads.reduce((sum, payload) => sum + LEGACY_BRUSH_HEADER_BYTES + payload.length, 4);
  const bytes = new Uint8Array(totalLength);
  const view = new DataView(bytes.buffer);
  let offset = 0;

  offset = writeUint16(view, offset, 2);
  offset = writeUint16(view, offset, brushes.length);

  for (let i = 0; i < brushes.length; i += 1) {
    offset = writeUint16(view, offset, 2);
    offset = writeInt32(view, offset, payloads[i].length);
    bytes.set(payloads[i], offset);
    offset += payloads[i].length;
  }

  return bytes.buffer;
}
